import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MessageCircle, Inbox, Send, Trash2, Pencil, Flag, Check, X, ShieldOff, Bell, BellOff, MessageSquare, Bookmark } from "lucide-react";
import { toast } from "sonner";

const REPORT_REASONS = ["HARASSMENT","SPAM","THREATS","IMPERSONATION","ACADEMIC_MISCONDUCT","INAPPROPRIATE_CONTENT","OTHER"];
const LABELS = { HARASSMENT:"Harassment", SPAM:"Spam", THREATS:"Threats", IMPERSONATION:"Impersonation", ACADEMIC_MISCONDUCT:"Academic misconduct", INAPPROPRIATE_CONTENT:"Inappropriate content", OTHER:"Other" };

export default function Messages() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "requests" ? "requests" : (params.get("tab") === "circles" ? "circles" : "messages"));
  const [convs, setConvs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [openId, setOpenId] = useState(null);
  // Phase 8: when a group thread is open, `openGroupId` holds the circle_id.
  // A group thread is mutually exclusive with a DM thread.
  const [openGroupId, setOpenGroupId] = useState(null);
  const [groupThread, setGroupThread] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("HARASSMENT");
  const [reportDesc, setReportDesc] = useState("");
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const openIdRef = useRef(null);
  const convsRef = useRef([]);
  const reconnectRef = useRef({ attempts: 0, timer: null });
  // Phase 7B — peers currently online (only those with whom I share an
  // ACTIVE dm_conversation; the server never fans out anything else).
  const [onlinePeers, setOnlinePeers] = useState(() => new Set());
  // Ephemeral realtime transport status. Rendered as a subtle pill; ONLY
  // shown while reconnecting. Contains no URLs, tokens, or infra details.
  const [wsStatus, setWsStatus] = useState("connecting"); // "connecting" | "connected" | "reconnecting"
  // Peers currently typing (server may only ever notify us about a peer
  // in a conversation we're a member of).
  const [typingByConv, setTypingByConv] = useState({}); // { [cid]: peerUserId }
  // Client-side safety net for typing indicators — if the server's
  // typing_stop never arrives, expire locally after this many ms.
  const typingExpireRef = useRef({}); // { [cid]: setTimeout handle }
  // Debounce for our OUTBOUND typing_start pings + local idle detection.
  const outTypingRef = useRef({ lastSentAt: 0, idleTimer: null, sentForConv: null });
  const openGroupIdRef = useRef(null);
  const groupsRef = useRef([]);
  useEffect(() => { openIdRef.current = openId; }, [openId]);
  useEffect(() => { openGroupIdRef.current = openGroupId; }, [openGroupId]);
  useEffect(() => { convsRef.current = convs; }, [convs]);
  useEffect(() => { groupsRef.current = groups; }, [groups]);

  const load = useCallback(async () => {
    try {
      const [c, r, g] = await Promise.all([
        api.get("/messages/conversations"),
        api.get("/messages/requests"),
        api.get("/groups/conversations"),
      ]);
      setConvs(c.data); setRequests(r.data); setGroups(g.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link support (from Search "Open in Context"):
  //   ?conversation=<id>  → switch to Messages tab + open DM thread
  //   ?circle=<id>        → switch to Circles tab + open group thread
  //   ?message=<mid>      → after thread hydrates, scroll to that message
  useEffect(() => {
    const convId = params.get("conversation");
    const circleId = params.get("circle");
    if (circleId) {
      setTab("circles");
      // guard against re-triggering when openGroup already matches
      if (openGroupIdRef.current !== circleId) openGroup(circleId);
    } else if (convId) {
      setTab("messages");
      if (openIdRef.current !== convId) openConv(convId);
    }
    // Optional scroll target — best effort, silent on missing.
    const mid = params.get("message");
    if (mid) {
      const attempts = [180, 500, 1000, 1800];
      attempts.forEach((delay) => setTimeout(() => {
        const el = document.querySelector(`[data-testid="msg-${mid}"], [data-testid="msg-group-${mid}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary");
          setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 2500);
        }
      }, delay));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const refreshOpenGroup = useCallback(async (circleId) => {
    try {
      const r = await api.get(`/groups/circles/${circleId}/messages`);
      setGroupThread(r.data);
      await api.post(`/groups/circles/${circleId}/read`).catch(() => {});
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      // Phase 10 — hydrate the caller's own bookmark set for this Circle
      try {
        const bm = await api.get("/messages/bookmarks", { params: { circle_id: circleId, limit: 100 } });
        const s = new Set((bm.data?.bookmarks || []).map((b) => b.message_id));
        setBookmarkedIds(s);
      } catch (_) { /* silent */ }
    } catch (_) { /* silent */ }
  }, []);
  const [bookmarkedIds, setBookmarkedIds] = useState(() => new Set());
  const toggleBookmark = async (mid) => {
    const isSaved = bookmarkedIds.has(mid);
    try {
      if (isSaved) {
        await api.delete(`/messages/${mid}/bookmark`);
        setBookmarkedIds((s) => { const n = new Set(s); n.delete(mid); return n; });
        toast("Removed from Saved");
      } else {
        await api.post(`/messages/${mid}/bookmark`);
        setBookmarkedIds((s) => { const n = new Set(s); n.add(mid); return n; });
        toast.success("Saved to your bookmarks");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ----- Phase 7: realtime WebSocket ----------------------------------------
  const refreshOpenThread = useCallback(async (cid) => {
    try {
      const r = await api.get(`/messages/conversations/${cid}`);
      setThread(r.data);
      await api.post(`/messages/conversations/${cid}/read`).catch(() => {});
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    } catch (_) { /* silent — REST fallback still works */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      const wsUrl = API.replace(/^http/, "ws") + "/ws/messaging";
      let ws;
      try { ws = new WebSocket(wsUrl); } catch (_) { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        reconnectRef.current.attempts = 0;
        setWsStatus("connected");
        // Resync on (re)connect so we don't miss anything that happened
        // while REST was the only fallback. Server has no subscription
        // replay, so this cannot duplicate the open thread — each message
        // is fetched by ID from REST.
        load();
        const cidNow = openIdRef.current;
        if (cidNow) refreshOpenThread(cidNow);
        const grpNow = openGroupIdRef.current;
        if (grpNow) refreshOpenGroup(grpNow);
      };
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch (_) { return; }
        const t = data?.type;
        // ---- new message -------------------------------------------------
        if (t === "message.new" && data.conversation_id) {
          const cid = data.conversation_id;
          // Message arrival implicitly ends any peer typing indicator
          setTypingByConv((m) => { if (!m[cid]) return m; const n = { ...m }; delete n[cid]; return n; });
          if (typingExpireRef.current[cid]) { clearTimeout(typingExpireRef.current[cid]); delete typingExpireRef.current[cid]; }
          if (cid === openIdRef.current) {
            refreshOpenThread(cid);
          } else {
            const peer = (convsRef.current.find((c) => c.id === cid) || {}).peer;
            const label = peer?.display_name ? `New message from ${peer.display_name}` : "New message";
            toast(label, { description: (data.preview || "").slice(0, 80), duration: 4000 });
          }
          load();
          return;
        }
        // ---- new group message ------------------------------------------
        if (t === "group.message.new" && data.circle_id) {
          const circleId = data.circle_id;
          const parentId = data.parent_message_id || null;
          // Muted Circle → no toast (attention is suppressed) but the
          // inbox list can still update its raw unread indicator via load().
          const g = (groupsRef.current || []).find((x) => x.circle_id === circleId);
          const isMuted = !!g?.muted;
          if (circleId === openGroupIdRef.current) {
            refreshOpenGroup(circleId);
            if (parentId && parentId === openThreadParentIdRef.current) {
              refreshOpenThreadReplies(parentId);
            }
          } else if (!isMuted) {
            const label = g?.circle_name ? `New in ${g.circle_name}` : "New Circle message";
            toast(label, { description: (data.preview || "").slice(0, 80), duration: 4000 });
          }
          load();
          return;
        }
        // ---- group edit / delete / reaction ------------------------------
        if (
          (t === "group.message.edited"
           || t === "group.message.deleted"
           || t === "group.message.reaction")
          && data.circle_id
        ) {
          if (data.circle_id === openGroupIdRef.current) {
            refreshOpenGroup(data.circle_id);
            if (openThreadParentIdRef.current) {
              refreshOpenThreadReplies(openThreadParentIdRef.current);
            }
          }
          return;
        }
        // ---- presence ---------------------------------------------------
        if (t === "presence.snapshot" && Array.isArray(data.online)) {
          setOnlinePeers(new Set(data.online));
          return;
        }
        if (t === "presence.update" && data.user_id) {
          setOnlinePeers((prev) => {
            const n = new Set(prev);
            if (data.online) n.add(data.user_id); else n.delete(data.user_id);
            return n;
          });
          return;
        }
        // ---- typing indicators ------------------------------------------
        if ((t === "typing_start" || t === "typing_stop") && data.conversation_id) {
          const cid = data.conversation_id;
          const peer = data.user_id;
          if (t === "typing_start" && peer) {
            setTypingByConv((m) => ({ ...m, [cid]: peer }));
            // Local safety net — expire the indicator if the peer disappears
            // without a typing_stop reaching us.
            if (typingExpireRef.current[cid]) clearTimeout(typingExpireRef.current[cid]);
            typingExpireRef.current[cid] = setTimeout(() => {
              setTypingByConv((m) => { if (!m[cid]) return m; const n = { ...m }; delete n[cid]; return n; });
              delete typingExpireRef.current[cid];
            }, 7000);
          } else {
            setTypingByConv((m) => { if (!m[cid]) return m; const n = { ...m }; delete n[cid]; return n; });
            if (typingExpireRef.current[cid]) { clearTimeout(typingExpireRef.current[cid]); delete typingExpireRef.current[cid]; }
          }
          return;
        }
      };
      ws.onclose = () => {
        // Prevent stale 'typing…' indicators on reconnect
        setTypingByConv({});
        Object.values(typingExpireRef.current).forEach((h) => clearTimeout(h));
        typingExpireRef.current = {};
        // Any presence we knew about is no longer authoritative until
        // the next snapshot arrives.
        setOnlinePeers(new Set());
        if (cancelled) return;
        setWsStatus("reconnecting");
        const attempts = ++reconnectRef.current.attempts;
        const delay = Math.min(1000 * 2 ** Math.min(attempts, 5), 15000);
        reconnectRef.current.timer = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      try { wsRef.current?.close(); } catch (_) {}
    };
  }, [load, refreshOpenThread, refreshOpenGroup]);

  const openConv = async (cid) => {
    // Leaving a previous conversation implicitly stops our typing state.
    if (openIdRef.current && openIdRef.current !== cid) {
      wsSendTypingStop(openIdRef.current);
    }
    setOpenGroupId(null);
    setGroupThread(null);
    setOpenId(cid);
    try {
      const r = await api.get(`/messages/conversations/${cid}`);
      setThread(r.data);
      await api.post(`/messages/conversations/${cid}/read`);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openGroup = async (circleId) => {
    if (openIdRef.current) wsSendTypingStop(openIdRef.current);
    setOpenId(null);
    setThread(null);
    setOpenGroupId(circleId);
    await refreshOpenGroup(circleId);
    await load();
  };

  const sendGroup = async () => {
    if (!body.trim() || !openGroupId) return;
    setBusy(true);
    try {
      await api.post(`/groups/circles/${openGroupId}/messages`, { body });
      setBody("");
      await refreshOpenGroup(openGroupId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ----- Phase 7B: outbound typing helpers ----------------------------------
  const wsSend = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    try { ws.send(JSON.stringify(payload)); } catch (_) {}
  }, []);

  const wsSendTypingStart = useCallback((cid) => {
    if (!cid) return;
    const now = Date.now();
    const state = outTypingRef.current;
    // Debounce: only re-send typing_start every 3s, or on conversation change.
    if (state.sentForConv === cid && now - state.lastSentAt < 3000) return;
    wsSend({ type: "typing_start", conversation_id: cid });
    state.sentForConv = cid;
    state.lastSentAt = now;
  }, [wsSend]);

  const wsSendTypingStop = useCallback((cid) => {
    if (!cid) return;
    const state = outTypingRef.current;
    if (state.idleTimer) { clearTimeout(state.idleTimer); state.idleTimer = null; }
    if (state.sentForConv === cid) {
      wsSend({ type: "typing_stop", conversation_id: cid });
      state.sentForConv = null;
      state.lastSentAt = 0;
    }
  }, [wsSend]);

  const onComposerKey = () => {
    const cid = openIdRef.current;
    if (!cid) return;
    wsSendTypingStart(cid);
    // Idle detection: stop typing 3s after last keystroke.
    if (outTypingRef.current.idleTimer) clearTimeout(outTypingRef.current.idleTimer);
    outTypingRef.current.idleTimer = setTimeout(() => wsSendTypingStop(cid), 3000);
  };

  // Component unmount / logout — proactively stop typing in the open conv.
  useEffect(() => () => {
    if (openIdRef.current) wsSendTypingStop(openIdRef.current);
    Object.values(typingExpireRef.current).forEach((h) => clearTimeout(h));
  }, [wsSendTypingStop]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const cid = openId;
    // Sending a message implicitly ends typing state on both sides.
    wsSendTypingStop(cid);
    try { await api.post(`/messages/conversations/${cid}/messages`, { body }); setBody(""); await openConv(cid); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveEdit = async (mid) => {
    try { await api.put(`/messages/conversations/${openId}/messages/${mid}`, { body: editBody }); setEditId(null); setEditBody(""); await openConv(openId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteMsg = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/messages/conversations/${openId}/messages/${mid}`); await openConv(openId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleDmReaction = async (mid, rt) => {
    try {
      await api.post(`/messages/conversations/${openId}/messages/${mid}/reactions`, { reaction_type: rt });
      await openConv(openId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const acceptReq = async (cid) => { try { await api.post(`/messages/requests/${cid}/accept`); toast.success("Request accepted."); await load(); setTab("messages"); openConv(cid); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const declineReq = async (cid) => { if (!window.confirm("Decline this request?")) return; try { await api.post(`/messages/requests/${cid}/decline`); toast.success("Request declined."); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const blockReq = async (cid) => { if (!window.confirm("Block this member?")) return; try { await api.post(`/messages/requests/${cid}/block`); toast.success("Member blocked."); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const submitReport = async () => {
    try {
      const r = await api.post(`/messages/messages/${reportFor}/report`, { reason: reportReason, description: reportDesc });
      if (r.data.already_reported) toast.info("You already reported this message.");
      else toast.success("Report submitted.");
      setReportFor(null); setReportReason("HARASSMENT"); setReportDesc("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activeList = tab === "requests" ? requests : convs;

  // Group message edit / delete / react ---------------------------------------
  const [groupEditId, setGroupEditId] = useState(null);
  const [groupEditBody, setGroupEditBody] = useState("");
  // Phase 8C — thread panel state. When set, the right side splits: main
  // Circle timeline on the left of the composer, thread panel overlay on
  // the right.
  const [openThreadParentId, setOpenThreadParentId] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [threadBody, setThreadBody] = useState("");
  // Phase 9 — report dialog state for Circle messages
  const [reportGroupMsg, setReportGroupMsg] = useState(null); // {id, body}
  const [reportGroupReason, setReportGroupReason] = useState("HARASSMENT");
  const [reportGroupNote, setReportGroupNote] = useState("");
  const submitGroupReport = async () => {
    if (!reportGroupMsg) return;
    try {
      const r = await api.post(`/moderation/circle-messages/${reportGroupMsg.id}/report`, {
        reason_code: reportGroupReason,
        reporter_note: reportGroupNote,
      });
      toast.success(r.data.already_reported ? "Report already recorded" : "Report submitted for review");
      setReportGroupMsg(null); setReportGroupNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openThreadParentIdRef = useRef(null);
  useEffect(() => { openThreadParentIdRef.current = openThreadParentId; }, [openThreadParentId]);

  const refreshOpenThreadReplies = useCallback(async (parentId) => {
    if (!parentId || !openGroupIdRef.current) return;
    try {
      const r = await api.get(`/groups/circles/${openGroupIdRef.current}/messages/${parentId}/thread`);
      setThreadData(r.data);
    } catch (_) { /* silent */ }
  }, []);

  const openThread = async (parentId) => {
    setOpenThreadParentId(parentId);
    await refreshOpenThreadReplies(parentId);
  };
  const closeThread = () => {
    setOpenThreadParentId(null);
    setThreadData(null);
    setThreadBody("");
  };
  const sendThreadReply = async () => {
    if (!threadBody.trim() || !openGroupId || !openThreadParentId) return;
    setBusy(true);
    try {
      await api.post(`/groups/circles/${openGroupId}/messages`, {
        body: threadBody,
        parent_message_id: openThreadParentId,
      });
      setThreadBody("");
      await Promise.all([
        refreshOpenThreadReplies(openThreadParentId),
        refreshOpenGroup(openGroupId),
      ]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleMute = async () => {
    if (!openGroupId) return;
    const current = (groupsRef.current || []).find((g) => g.circle_id === openGroupId);
    const newVal = !(current?.muted);
    try {
      await api.put(`/groups/circles/${openGroupId}/mute`, { muted: newVal });
      await load();
      toast.success(newVal ? "Circle muted" : "Circle unmuted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveGroupEdit = async (mid) => {
    try {
      await api.put(`/groups/circles/${openGroupId}/messages/${mid}`, { body: groupEditBody });
      setGroupEditId(null); setGroupEditBody("");
      await refreshOpenGroup(openGroupId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteGroupMsg = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/groups/circles/${openGroupId}/messages/${mid}`);
      await refreshOpenGroup(openGroupId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleGroupReaction = async (mid, rt) => {
    try {
      await api.post(`/groups/circles/${openGroupId}/messages/${mid}/reactions`, { reaction_type: rt });
      await refreshOpenGroup(openGroupId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex h-[calc(100vh-0px)] max-h-screen" data-testid="messages-page">
      <div className="w-80 border-r border-border shrink-0 flex flex-col">
        <div className="p-6 border-b border-border">
          <p className="text-xs uppercase tracking-[0.25em] text-primary">Inbox</p>
          <div className="flex items-center gap-2">
            <h1 className="font-serif text-2xl font-black tracking-tight mt-1">Messages</h1>
            {wsStatus === "reconnecting" && (
              <span
                className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground"
                title="Realtime updates are temporarily unavailable. Messages still send and receive over the web."
                data-testid="msg-conn-status-reconnecting"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Reconnecting…
              </span>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => setTab("messages")} data-testid="msg-tab-messages" className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest ${tab === "messages" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>DMs</button>
            <button onClick={() => setTab("circles")} data-testid="msg-tab-circles" className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest ${tab === "circles" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
              Circles {groups.filter((g) => g.unread).length > 0 && <span className="ml-1 opacity-80">•</span>}
            </button>
            <button onClick={() => setTab("requests")} data-testid="msg-tab-requests" className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest ${tab === "requests" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
              Requests {requests.length > 0 && <span className="ml-1 opacity-80">{requests.length}</span>}
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid={tab === "requests" ? "msg-requests-list" : (tab === "circles" ? "msg-groups-list" : "msg-conv-list")}>
          {tab === "circles" ? (
            groups.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="msg-empty-groups">
                <Inbox className="h-6 w-6 mx-auto text-muted-foreground/60" />
                <p className="mt-3">No Circle conversations yet.</p>
                <p className="mt-1 text-xs">Join a Circle to start collaborating with peers.</p>
              </div>
            ) : groups.map((g) => (
              <button key={g.circle_id} onClick={() => openGroup(g.circle_id)} data-testid={`msg-group-${g.circle_id}`} className={`w-full text-left p-4 border-b border-border hover:bg-secondary/50 ${openGroupId === g.circle_id ? "bg-secondary" : ""}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {g.muted && <BellOff className="h-3 w-3 text-muted-foreground shrink-0" data-testid={`msg-group-muted-${g.circle_id}`} />}
                    <p className="font-medium truncate">{g.circle_name}</p>
                  </div>
                  {g.unread && !g.muted && <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid={`msg-group-unread-${g.circle_id}`} />}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-primary/70">Circle{g.muted ? " · Muted" : ""}</p>
                {g.last_message_preview && <p className="text-xs text-muted-foreground truncate mt-1">{g.last_message_preview}</p>}
              </button>
            ))
          ) : activeList.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="msg-empty">
              <Inbox className="h-6 w-6 mx-auto text-muted-foreground/60" />
              <p className="mt-3">{tab === "requests" ? "No message requests yet." : "No conversations yet."}</p>
              <p className="mt-1 text-xs">{tab === "requests" ? "New first-contact requests will appear here." : "Reach out from a peer profile or a Circle."}</p>
            </div>
          ) : activeList.map((c) => (
            <button key={c.id} onClick={() => openConv(c.id)} data-testid={`msg-conv-${c.id}`} className={`w-full text-left p-4 border-b border-border hover:bg-secondary/50 ${openId === c.id ? "bg-secondary" : ""}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  {onlinePeers.has(c.peer.id) && (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0"
                      title="Online now"
                      data-testid={`msg-presence-online-${c.peer.id}`}
                    />
                  )}
                  <p className="font-medium truncate">{c.peer.display_name}</p>
                </div>
                {c.unread && <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid={`msg-unread-${c.id}`} />}
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{[c.peer.degree_type, c.peer.discipline].filter(Boolean).join(" · ")}</p>
              {c.last_message_preview && <p className="text-xs text-muted-foreground truncate mt-1">{c.last_message_preview}</p>}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {groupThread ? (
          <>
            <div className="p-6 border-b border-border flex items-start justify-between gap-4" data-testid="msg-group-thread-header">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-primary">Circle Conversation</p>
                <h2 className="font-serif text-2xl font-black tracking-tight mt-1">{groupThread.circle_name}</h2>
                <p className="text-xs text-muted-foreground mt-1">Every ACTIVE member of this Circle can see messages here.</p>
              </div>
              {(() => {
                const g = groups.find((x) => x.circle_id === openGroupId);
                const isMuted = !!g?.muted;
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleMute}
                    className="rounded-full shrink-0"
                    data-testid="msg-group-mute-toggle"
                    title={isMuted ? "Unmute this Circle" : "Mute this Circle"}
                  >
                    {isMuted ? <BellOff className="h-3.5 w-3.5 mr-1.5" /> : <Bell className="h-3.5 w-3.5 mr-1.5" />}
                    {isMuted ? "Muted" : "Mute"}
                  </Button>
                );
              })()}
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="msg-group-thread-body">
              {groupThread.messages.map((m) => (
                <div key={m.id} className={`flex ${m.is_own ? "justify-end" : "justify-start"}`} data-testid={`msg-group-${m.id}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${m.is_own ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {!m.is_own && <p className="text-[11px] font-medium opacity-80 mb-0.5">{m.sender_display}</p>}
                    {m.is_deleted ? (
                      <p className="text-xs italic opacity-70" data-testid={`msg-group-deleted-${m.id}`}>Message deleted</p>
                    ) : m.moderation_removed ? (
                      <p className="text-xs italic opacity-70" data-testid={`msg-group-modremoved-${m.id}`}>Message removed by moderation</p>
                    ) : groupEditId === m.id ? (
                      <div>
                        <Textarea value={groupEditBody} onChange={(e) => setGroupEditBody(e.target.value)} maxLength={5000} className="min-h-[60px] text-foreground bg-background" data-testid={`msg-group-edit-body-${m.id}`} />
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" onClick={() => saveGroupEdit(m.id)} className="rounded-full" data-testid={`msg-group-save-${m.id}`}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => { setGroupEditId(null); setGroupEditBody(""); }} className="rounded-full">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.body}</p>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[10px] opacity-70">
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                      {m.status === "EDITED" && !m.is_deleted && <span data-testid={`msg-group-edited-${m.id}`}>· edited</span>}
                      {m.is_own && !m.is_deleted && groupEditId !== m.id && (
                        <>
                          <button onClick={() => { setGroupEditId(m.id); setGroupEditBody(m.body); }} data-testid={`msg-group-edit-${m.id}`}><Pencil className="h-3 w-3" /></button>
                          <button onClick={() => deleteGroupMsg(m.id)} data-testid={`msg-group-delete-${m.id}`}><Trash2 className="h-3 w-3" /></button>
                        </>
                      )}
                    </div>
                    {!m.is_deleted && m.reactions && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {["HELPFUL", "INSIGHTFUL", "SUPPORT"].map((rt) => {
                          const count = m.reactions.counts?.[rt] || 0;
                          const mine = (m.reactions.mine || []).includes(rt);
                          return (
                            <button
                              key={rt}
                              onClick={() => toggleGroupReaction(m.id, rt)}
                              data-testid={`msg-group-react-${rt.toLowerCase()}-${m.id}`}
                              className={`text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 transition-colors ${
                                mine
                                  ? "bg-primary/20 border-primary/60 text-primary-foreground"
                                  : "bg-background/40 border-border text-foreground/70 hover:bg-background/60"
                              }`}
                            >
                              {rt === "HELPFUL" ? "Helpful" : rt === "INSIGHTFUL" ? "Insightful" : "Support"}
                              {count > 0 && <span className="ml-1 font-medium">{count}</span>}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => openThread(m.id)}
                          data-testid={`msg-group-thread-open-${m.id}`}
                          className="text-[10px] uppercase tracking-widest rounded-full border border-border bg-background/40 text-foreground/70 px-2 py-0.5 hover:bg-background/60 inline-flex items-center gap-1"
                        >
                          <MessageSquare className="h-3 w-3" />
                          {(m.reply_count || 0) > 0 ? `${m.reply_count} ${m.reply_count === 1 ? "reply" : "replies"}` : "Reply"}
                        </button>
                        {!m.is_own && (
                          <button
                            onClick={() => setReportGroupMsg({ id: m.id, body: m.body })}
                            data-testid={`msg-group-report-${m.id}`}
                            className="text-[10px] uppercase tracking-widest rounded-full border border-border bg-background/40 text-foreground/70 px-2 py-0.5 hover:bg-destructive/20 inline-flex items-center gap-1"
                            title="Report to Circle moderators"
                          >
                            <Flag className="h-3 w-3" />
                            Report
                          </button>
                        )}
                        <button
                          onClick={() => toggleBookmark(m.id)}
                          data-testid={`msg-group-bookmark-${m.id}`}
                          className={`text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 inline-flex items-center gap-1 transition-colors ${
                            bookmarkedIds.has(m.id)
                              ? "bg-primary/20 border-primary/60 text-primary-foreground"
                              : "border-border bg-background/40 text-foreground/70 hover:bg-background/60"
                          }`}
                          title={bookmarkedIds.has(m.id) ? "Remove bookmark" : "Bookmark this message privately"}
                        >
                          <Bookmark className="h-3 w-3" />
                          {bookmarkedIds.has(m.id) ? "Saved" : "Save"}
                        </button>
                      </div>
                    )}
                    {/* Phase 11 — inline thread preview (max 2 replies) */}
                    {!m.is_deleted && !m.moderation_removed && (m.thread_preview || []).length > 0 && (
                      <div
                        className="mt-3 border-l-2 border-border/60 pl-3 space-y-1.5"
                        data-testid={`msg-group-thread-preview-${m.id}`}
                        aria-label={`Latest replies to this message`}
                      >
                        {(m.thread_preview || []).map((rep) => (
                          <button
                            key={rep.id}
                            onClick={() => openThread(m.id)}
                            data-testid={`msg-group-thread-preview-reply-${rep.id}`}
                            className="block w-full text-left text-[11px] rounded-md hover:bg-background/40 px-1.5 py-1 -mx-1.5"
                          >
                            {rep.is_deleted ? (
                              <span className="italic opacity-70">
                                <span className="font-medium not-italic">{rep.sender_display}:</span>{" "}
                                Message deleted
                              </span>
                            ) : rep.moderation_removed ? (
                              <span className="italic opacity-70">
                                <span className="font-medium not-italic">{rep.sender_display}:</span>{" "}
                                Message removed by moderation
                              </span>
                            ) : (
                              <span>
                                <span className="font-medium">{rep.sender_display}:</span>{" "}
                                <span className="opacity-90 line-clamp-2">{rep.body}</span>
                              </span>
                            )}
                          </button>
                        ))}
                        {(m.reply_count || 0) > (m.thread_preview || []).length && (
                          <button
                            onClick={() => openThread(m.id)}
                            data-testid={`msg-group-thread-preview-viewall-${m.id}`}
                            className="text-[10px] uppercase tracking-widest text-foreground/70 hover:text-foreground underline underline-offset-2 mt-0.5"
                          >
                            View all {m.reply_count} replies
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="p-4 border-t border-border" data-testid="msg-group-composer">
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write to the Circle…"
                maxLength={5000}
                className="min-h-[60px]"
                data-testid="msg-group-composer-body"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendGroup(); }}
              />
              <div className="mt-2 flex justify-end">
                <Button onClick={sendGroup} disabled={busy || !body.trim()} className="rounded-full" data-testid="msg-group-composer-send"><Send className="h-3 w-3 mr-1" /> Send</Button>
              </div>
            </div>
          </>
        ) : !thread ? (
          <div className="flex-1 flex items-center justify-center p-10 text-center">
            <div>
              <MessageCircle className="h-10 w-10 text-muted-foreground/60 mx-auto" />
              <p className="mt-4 font-serif text-2xl font-black tracking-tight">Select a conversation</p>
              <p className="text-sm text-muted-foreground mt-2">Private 1-to-1 scholarly conversation. Every first contact is a request the recipient must accept.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-border" data-testid="msg-thread-header">
              <p className="text-xs uppercase tracking-[0.25em] text-primary">{thread.status === "PENDING" ? "Message request" : "Conversation"}</p>
              <div className="flex items-center gap-2 mt-1">
                <h2 className="font-serif text-2xl font-black tracking-tight">{thread.peer.display_name}</h2>
                {onlinePeers.has(thread.peer.id) && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600"
                    data-testid={`msg-presence-online-header-${thread.peer.id}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{[thread.peer.degree_type, thread.peer.discipline, thread.peer.methodology].filter(Boolean).join(" · ")}</p>
              {(thread.context?.match_band || thread.context?.shared_circles_count) && (
                <p className="text-[11px] text-primary/80 mt-2">
                  {thread.context.match_band && "Connected through People Walking Your Path."}
                  {thread.context.shared_circles_count > 0 && <> You share {thread.context.shared_circles_count} {thread.context.shared_circles_count === 1 ? "Circle" : "Circles"}.</>}
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="msg-thread-body">
              {thread.messages.map((m) => (
                <div key={m.id} className={`flex ${m.is_own ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${m.is_own ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {m.is_deleted ? (
                      <p className="text-xs italic opacity-70" data-testid={`msg-deleted-${m.id}`}>Message deleted</p>
                    ) : editId === m.id ? (
                      <div>
                        <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} maxLength={5000} className="min-h-[60px] text-foreground bg-background" data-testid={`msg-edit-body-${m.id}`} />
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" onClick={() => saveEdit(m.id)} className="rounded-full" data-testid={`msg-save-${m.id}`}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditId(null); setEditBody(""); }} className="rounded-full">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.body}</p>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[10px] opacity-70">
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                      {m.status === "EDITED" && <span>· edited</span>}
                      {m.is_own && !m.is_deleted && editId !== m.id && (
                        <>
                          <button onClick={() => { setEditId(m.id); setEditBody(m.body); }} data-testid={`msg-edit-${m.id}`}><Pencil className="h-3 w-3" /></button>
                          <button onClick={() => deleteMsg(m.id)} data-testid={`msg-delete-${m.id}`}><Trash2 className="h-3 w-3" /></button>
                        </>
                      )}
                      {!m.is_own && !m.is_deleted && (
                        <button onClick={() => setReportFor(m.id)} data-testid={`msg-report-${m.id}`}><Flag className="h-3 w-3" /></button>
                      )}
                    </div>
                    {!m.is_deleted && m.reactions && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`msg-dm-reactions-${m.id}`}>
                        {["HELPFUL", "INSIGHTFUL", "SUPPORT"].map((rt) => {
                          const count = m.reactions.counts?.[rt] || 0;
                          const mine = (m.reactions.mine || []).includes(rt);
                          return (
                            <button
                              key={rt}
                              onClick={() => toggleDmReaction(m.id, rt)}
                              data-testid={`msg-dm-react-${rt.toLowerCase()}-${m.id}`}
                              className={`text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 transition-colors ${
                                mine
                                  ? "bg-primary/20 border-primary/60 text-primary-foreground"
                                  : "bg-background/40 border-border text-foreground/70 hover:bg-background/60"
                              }`}
                            >
                              {rt === "HELPFUL" ? "Helpful" : rt === "INSIGHTFUL" ? "Insightful" : "Support"}
                              {count > 0 && <span className="ml-1 font-medium">{count}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {thread.status === "ACTIVE" && typingByConv[openId] === thread.peer.id && (
              <div
                className="px-6 pb-1 text-[11px] italic text-muted-foreground"
                data-testid={`msg-typing-${openId}`}
              >
                {thread.peer.display_name} is typing…
              </div>
            )}
            {thread.status === "ACTIVE" ? (
              <div className="p-4 border-t border-border" data-testid="msg-composer">
                <Textarea
                  value={body}
                  onChange={(e) => { setBody(e.target.value); onComposerKey(); }}
                  placeholder="Write a scholarly reply…"
                  maxLength={5000}
                  className="min-h-[60px]"
                  data-testid="msg-composer-body"
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                />
                <div className="mt-2 flex justify-end">
                  <Button onClick={send} disabled={busy || !body.trim()} className="rounded-full" data-testid="msg-composer-send"><Send className="h-3 w-3 mr-1" /> Send</Button>
                </div>
              </div>
            ) : thread.status === "PENDING" ? (
              <div className="p-4 border-t border-border flex flex-wrap gap-2 justify-end" data-testid="msg-request-actions">
                <Button variant="outline" size="sm" onClick={() => blockReq(openId)} className="rounded-full" data-testid="msg-request-block"><ShieldOff className="h-3 w-3 mr-1" /> Block</Button>
                <Button variant="outline" size="sm" onClick={() => declineReq(openId)} className="rounded-full" data-testid="msg-request-decline"><X className="h-3 w-3 mr-1" /> Decline</Button>
                <Button size="sm" onClick={() => acceptReq(openId)} className="rounded-full" data-testid="msg-request-accept"><Check className="h-3 w-3 mr-1" /> Accept</Button>
              </div>
            ) : (
              <div className="p-4 border-t border-border text-xs text-muted-foreground text-center" data-testid="msg-conv-closed">This conversation is no longer active.</div>
            )}
          </>
        )}
      </div>

      {openThreadParentId && threadData && (
        <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-background border-l border-border shadow-2xl z-40 flex flex-col" data-testid="msg-thread-panel">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Thread</p>
              <p className="text-sm font-medium truncate">{threadData.parent?.sender_display}</p>
            </div>
            <button onClick={closeThread} data-testid="msg-thread-close" className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="msg-thread-body-panel">
            <div className="rounded-2xl border border-border bg-secondary/40 p-3">
              <p className="text-[11px] font-medium opacity-80 mb-0.5">{threadData.parent?.sender_display}</p>
              {threadData.parent?.is_deleted ? (
                <p className="text-xs italic opacity-70">Message deleted</p>
              ) : (
                <p className="whitespace-pre-wrap text-sm">{threadData.parent?.body}</p>
              )}
              <p className="text-[10px] opacity-60 mt-1">{new Date(threadData.parent?.created_at).toLocaleString()}</p>
            </div>
            {(threadData.replies || []).map((r) => (
              <div key={r.id} className="rounded-2xl bg-secondary/40 p-3" data-testid={`msg-thread-reply-${r.id}`}>
                <p className="text-[11px] font-medium opacity-80 mb-0.5">{r.sender_display}</p>
                {r.is_deleted ? (
                  <p className="text-xs italic opacity-70">Message deleted</p>
                ) : (
                  <p className="whitespace-pre-wrap text-sm">{r.body}</p>
                )}
                <p className="text-[10px] opacity-60 mt-1">
                  {new Date(r.created_at).toLocaleString()}{r.status === "EDITED" && !r.is_deleted ? " · edited" : ""}
                </p>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border">
            <Textarea
              value={threadBody}
              onChange={(e) => setThreadBody(e.target.value)}
              placeholder="Reply in thread…"
              maxLength={5000}
              className="min-h-[52px]"
              data-testid="msg-thread-composer-body"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendThreadReply(); }}
            />
            <div className="mt-2 flex justify-end">
              <Button onClick={sendThreadReply} disabled={busy || !threadBody.trim()} className="rounded-full" data-testid="msg-thread-composer-send">
                <Send className="h-3 w-3 mr-1" /> Reply
              </Button>
            </div>
          </div>
        </div>
      )}

      {reportGroupMsg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="msg-group-report-dialog">
          <div className="bg-background rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-serif text-xl font-black tracking-tight">Report to Circle moderators</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Circle owners will review this report. Your identity is not shared with the message author.
            </p>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4 block">Reason</label>
            <select
              value={reportGroupReason}
              onChange={(e) => setReportGroupReason(e.target.value)}
              className="mt-1 w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm"
              data-testid="msg-group-report-reason"
            >
              <option value="HARASSMENT">Harassment</option>
              <option value="THREAT_OR_ABUSE">Threat or abuse</option>
              <option value="SPAM">Spam</option>
              <option value="IMPERSONATION">Impersonation</option>
              <option value="PRIVACY_CONCERN">Privacy concern</option>
              <option value="ACADEMIC_INTEGRITY">Academic integrity</option>
              <option value="INAPPROPRIATE_CONTENT">Inappropriate content</option>
              <option value="OTHER">Other</option>
            </select>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4 block">
              Optional context
            </label>
            <Textarea
              value={reportGroupNote}
              onChange={(e) => setReportGroupNote(e.target.value)}
              maxLength={500}
              placeholder="Optional short note"
              className="mt-1 min-h-[80px]"
              data-testid="msg-group-report-note"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setReportGroupMsg(null); setReportGroupNote(""); }} className="rounded-full" data-testid="msg-group-report-cancel">Cancel</Button>
              <Button onClick={submitGroupReport} className="rounded-full" data-testid="msg-group-report-submit">Submit report</Button>
            </div>
          </div>
        </div>
      )}

      {reportFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-6" data-testid="msg-report-modal">
          <Card className="max-w-md w-full p-8">
            <h3 className="font-serif text-xl font-black tracking-tight">Report this message</h3>
            <p className="text-xs text-muted-foreground mt-1">Only the reported message and narrow conversation context become available to platform moderation. Your full history is never handed over.</p>
            <div className="mt-6 space-y-2">
              {REPORT_REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="reason" value={r} checked={reportReason === r} onChange={() => setReportReason(r)} data-testid={`msg-report-reason-${r}`} />
                  {LABELS[r]}
                </label>
              ))}
            </div>
            <Textarea value={reportDesc} onChange={(e) => setReportDesc(e.target.value)} placeholder="Add context (optional)" maxLength={1000} className="mt-4" data-testid="msg-report-desc" />
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setReportFor(null)} className="rounded-full" data-testid="msg-report-cancel">Cancel</Button>
              <Button onClick={submitReport} className="rounded-full" data-testid="msg-report-submit">Submit report</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
